import {
  Account_Read_Nested,
  LineItemType_Read,
  Rutter_Connection_Owner_Export_Settings,
  Rutter_Connection_Read,
  Rutter_Customer_Read,
  Rutter_Item_Read,
  Rutter_Itemizable_Read,
  Rutter_ItemMapping_Read,
  Rutter_ItemRule_Read,
  Rutter_ItemRuleBacktest,
  Rutter_LedgerAccount_Read,
  Rutter_LineItemMatch_Read,
  Rutter_MappedRecord_Read_Nested,
  Rutter_PaymentTerm_Read,
  Rutter_TaxRate_MappedRecord_Read_Nested,
  Rutter_TaxRate_Read,
  Rutter_Vendor_Read,
  RutterApi_Customer_Read,
  RutterApi_Item_Read,
  RutterApi_LedgerAccount_Read,
  RutterApi_PaymentTerm_Read,
  RutterApi_TaxRate_Read,
  RutterApi_Vendor_Read,
  RutterItemRuleLineKind,
  RutterLedgerAccountType,
  RutterLineItemMatchConfidence,
  RutterLineItemMatchSource,
  SettlementExportSide,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';

import { CompanyBasic } from '~hooks/useCompany/models';
import { PaymentTermBasic } from '~hooks/usePaymentTerms/models';
import { TaxRateScheduleBasic } from '~hooks/useTaxRateSchedule/models';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import isEnumValue from '~utils/isEnumValue';

// Rutter platform enum values; QUICKBOOKS is QuickBooks Online
export const RUTTER_PLATFORM_QUICK_BOOKS = 'QUICKBOOKS';
export const RUTTER_PLATFORM_QUICK_BOOKS_DESKTOP = 'QUICKBOOKS_DESKTOP';

const ACTIVE_RUTTER_CONNECTION_STATUS = 'active';

// QuickBooks reports record statuses as lowercase strings, eg "active" or "archived"
const ACTIVE_RUTTER_RECORD_STATUS = 'active';

class RutterOwnerExportSettings {
  constructor(
    private _accounting_platform_assigns_document_number: boolean,
    private _accounting_export_populates_invoice_service_date: boolean,
    private _line_description_formats: Rutter_Connection_Owner_Export_Settings['line_description_formats'],
  ) {}

  public get accountingPlatformAssignsDocumentNumber() {
    return this._accounting_platform_assigns_document_number;
  }

  public get accountingExportPopulatesInvoiceServiceDate() {
    return this._accounting_export_populates_invoice_service_date;
  }

  public get lineDescriptionFormats() {
    return this._line_description_formats;
  }

  public static parse(proto: Rutter_Connection_Owner_Export_Settings) {
    return new RutterOwnerExportSettings(
      proto.accounting_platform_assigns_document_number,
      proto.accounting_export_populates_invoice_service_date,
      proto.line_description_formats,
    );
  }
}

export class RutterConnection {
  constructor(
    private _id: string,
    private _status: Nullable<string>,
    private _platform: Nullable<string>,
    private _link_url: Nullable<string>,
    private _synchronized_at: Nullable<Dayjs>,
    private _owner_company: CompanyBasic,
    private _shared_with_child_companies: boolean,
    private _managed_by_owner: boolean,
    private _owner_export_settings: RutterOwnerExportSettings,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get status(): Nullable<string> {
    return this._status;
  }

  public get platform(): Nullable<string> {
    return this._platform;
  }

  public get linkUrl(): Nullable<string> {
    return this._link_url;
  }

  public get synchronizedAt(): Nullable<Dayjs> {
    return this._synchronized_at;
  }

  public get isConnected() {
    return this._status === ACTIVE_RUTTER_CONNECTION_STATUS;
  }

  public get isQuickBooksDesktop() {
    return this._platform === RUTTER_PLATFORM_QUICK_BOOKS_DESKTOP;
  }

  public get ownerCompany() {
    return this._owner_company;
  }

  public get sharedWithChildCompanies() {
    return this._shared_with_child_companies;
  }

  public get managedByOwner() {
    return this._managed_by_owner;
  }

  public get ownerExportSettings() {
    return this._owner_export_settings;
  }

  public get discardedAt(): Nullable<Dayjs> {
    return this._discarded_at;
  }

  public static parse(proto: Rutter_Connection_Read) {
    return new RutterConnection(
      proto.id,
      proto.status ?? null,
      proto.platform ?? null,
      proto.link_url ?? null,
      proto.synchronized_at ? dayjs(proto.synchronized_at) : null,
      CompanyBasic.parse(proto.owner_company),
      proto.shared_with_child_companies,
      proto.managed_by_owner,
      RutterOwnerExportSettings.parse(proto.owner_export_settings),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class RutterMappedRecord {
  constructor(
    private _id: string,
    private _name: Nullable<string>,
    private _is_active: boolean = true,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  // a record already mapped to a row is shown whatever its status; only unmapped choices
  // are limited to active ones
  public get isActive() {
    return this._is_active;
  }

  public get label() {
    return this._name ?? '';
  }

  public static parse(proto: Rutter_MappedRecord_Read_Nested) {
    return new RutterMappedRecord(proto.id, proto.name);
  }
}

export class RutterTaxRateMappedRecord extends RutterMappedRecord {
  constructor(
    id: string,
    name: Nullable<string>,
    private _total_tax_rate: Nullable<string>,
    isActive: boolean = true,
  ) {
    super(id, name, isActive);
  }

  // the rate as a percentage (13 for HST ON), null when QuickBooks reports none
  public get totalTaxRate() {
    return this._total_tax_rate;
  }

  public get label() {
    return this._total_tax_rate === null
      ? (this.name ?? '')
      : t(
          'administration.integration.rutter.quick_books_online.tread_first.tax_rate_option',
          {
            name: this.name ?? '',
            rate: Number(this._total_tax_rate),
          },
        );
  }

  public static parse(proto: Rutter_TaxRate_MappedRecord_Read_Nested) {
    return new RutterTaxRateMappedRecord(proto.id, proto.name, proto.total_tax_rate);
  }
}

export class RutterCustomer {
  constructor(
    private _id: string,
    private _rutterApiCustomer: RutterApi_Customer_Read,
    private _customerAccount: Nullable<Account_Read_Nested>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get rutterApiCustomer() {
    return this._rutterApiCustomer;
  }

  public get customerAccount(): Nullable<Account_Read_Nested> {
    return this._customerAccount;
  }

  public get isActive() {
    return this._rutterApiCustomer.status === ACTIVE_RUTTER_RECORD_STATUS;
  }

  public get discardedAt(): Nullable<Dayjs> {
    return this._discarded_at;
  }

  public static parse(proto: Rutter_Customer_Read) {
    return new RutterCustomer(
      proto.id,
      proto.data,
      proto.customer_account ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class RutterVendor {
  constructor(
    private _id: string,
    private _rutterApiVendor: RutterApi_Vendor_Read,
    private _vendorAccount: Nullable<Account_Read_Nested>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get rutterApiVendor() {
    return this._rutterApiVendor;
  }

  public get vendorAccount(): Nullable<Account_Read_Nested> {
    return this._vendorAccount;
  }

  public get isActive() {
    return this._rutterApiVendor.status === ACTIVE_RUTTER_RECORD_STATUS;
  }

  public get discardedAt(): Nullable<Dayjs> {
    return this._discarded_at;
  }

  public static parse(proto: Rutter_Vendor_Read) {
    return new RutterVendor(
      proto.id,
      proto.data,
      proto.vendor_account ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

const ACTIVE_QBO_ITEM_STATUS = 'active';
const QBO_CATEGORY_ITEM_TYPE = 'Category';

export class RutterItem {
  constructor(
    private _id: string,
    private _rutterApiItem: RutterApi_Item_Read,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get rutterApiItem() {
    return this._rutterApiItem;
  }

  public get discardedAt(): Nullable<Dayjs> {
    return this._discarded_at;
  }

  // active, non-Category QuickBooks items are the ones a line can be billed to
  public get isSelectable() {
    return (
      !this._discarded_at &&
      this._rutterApiItem?.status === ACTIVE_QBO_ITEM_STATUS &&
      this._rutterApiItem?.type !== QBO_CATEGORY_ITEM_TYPE
    );
  }

  public static parse(proto: Rutter_Item_Read) {
    return new RutterItem(
      proto.id,
      proto.data,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class RutterLedgerAccount {
  constructor(
    private _id: string,
    private _rutterApiLedgerAccount: RutterApi_LedgerAccount_Read,
    private _type: Nullable<RutterLedgerAccountType>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get rutterApiLedgerAccount() {
    return this._rutterApiLedgerAccount;
  }

  public get type(): Nullable<RutterLedgerAccountType> {
    return this._type;
  }

  public get discardedAt(): Nullable<Dayjs> {
    return this._discarded_at;
  }

  public static parse(proto: Rutter_LedgerAccount_Read) {
    return new RutterLedgerAccount(
      proto.id,
      proto.data,
      proto.type ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class RutterPaymentTerm {
  constructor(
    private _id: string,
    private _rutter_api_payment_term: RutterApi_PaymentTerm_Read,
    private _payment_term: Nullable<PaymentTermBasic> = null,
  ) {}

  public get id() {
    return this._id;
  }

  // The accounting platform's own id, which is what a settlement export stores in
  // external_payment_terms_id. Distinct from `id`, which identifies the imported record.
  public get externalId() {
    return this._rutter_api_payment_term.id;
  }

  public get termName() {
    return this._rutter_api_payment_term.term_name ?? null;
  }

  public get dueDays() {
    return this._rutter_api_payment_term.due_days ?? null;
  }

  // The Tread-side term this imported one maps to, not a field of the imported record.
  public get paymentTerm() {
    return this._payment_term;
  }

  public static parse(proto: Rutter_PaymentTerm_Read) {
    return new RutterPaymentTerm(
      proto.id,
      proto.data,
      proto.payment_term ? PaymentTermBasic.parse(proto.payment_term) : null,
    );
  }
}

// Rutter reports a tax rate's status as active, inactive or unknown

export class RutterTaxRate {
  constructor(
    private _id: string,
    private _rutter_api_tax_rate: RutterApi_TaxRate_Read,
    private _tax_rate_schedule: Nullable<TaxRateScheduleBasic> = null,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get rutterApiTaxRate() {
    return this._rutter_api_tax_rate;
  }

  public get name() {
    return this._rutter_api_tax_rate.name ?? null;
  }

  public get code() {
    return this._rutter_api_tax_rate.code ?? null;
  }

  public get status() {
    return this._rutter_api_tax_rate.status;
  }

  public get isActive() {
    return this._rutter_api_tax_rate.status === ACTIVE_RUTTER_RECORD_STATUS;
  }

  // the rate as a percentage (13 for HST ON), null when QuickBooks reports none
  public get totalTaxRate() {
    return this._rutter_api_tax_rate.total_tax_rate ?? null;
  }

  // The Tread-side schedule this imported rate maps to, not a field of the imported record.
  public get taxRateSchedule() {
    return this._tax_rate_schedule;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: Rutter_TaxRate_Read) {
    return new RutterTaxRate(
      proto.id,
      proto.data,
      proto.tax_rate_schedule
        ? TaxRateScheduleBasic.parse(proto.tax_rate_schedule)
        : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class RutterItemMapping {
  constructor(
    private _id: string,
    private _rutter_item_id: string,
    private _rutter_itemizable: Rutter_Itemizable_Read,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get rutterItemId() {
    return this._rutter_item_id;
  }

  public get rutterItemizable() {
    return this._rutter_itemizable;
  }

  public get discardedAt(): Nullable<Dayjs> {
    return this._discarded_at;
  }

  public static parse(proto: Rutter_ItemMapping_Read) {
    return new RutterItemMapping(
      proto.id,
      proto.rutter_item_id,
      proto.rutter_itemizable,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class LineItemTypeOption extends ItemNameAndId {
  constructor(proto: LineItemType_Read) {
    super(proto.name, proto.id);
  }

  public static parse(proto: LineItemType_Read): LineItemTypeOption {
    return new LineItemTypeOption(proto);
  }
}

// Only matches the LLM rules picked get the AI marker; direct mappings are the
// company's own mapping table and manual picks are the user's.
const isAiSuggestedBySource = {
  [RutterLineItemMatchSource.RULE]: true,
  [RutterLineItemMatchSource.DIRECT_MAPPING]: false,
  [RutterLineItemMatchSource.MANUAL]: false,
} satisfies Record<RutterLineItemMatchSource, boolean>;

// The settlement line or add-on a row exports, so a pick can be saved before it has a match record.
export interface QuickBooksItemLine {
  addOnChargeId?: string;
  settlementLineItemId?: string;
}

export class RutterLineItemMatch {
  constructor(
    private _id: string,
    private _settlement_id: string,
    private _settlement_line_item_id: Nullable<string>,
    private _add_on_charge_id: Nullable<string>,
    private _rutter_item_id: Nullable<string>,
    private _source: Nullable<RutterLineItemMatchSource>,
    private _reasoning: Nullable<string>,
    private _confidence: Nullable<RutterLineItemMatchConfidence>,
    private _issues: string[],
    private _rutter_item: Nullable<RutterItem>,
    private _updated_at: Dayjs,
  ) {}

  public get id() {
    return this._id;
  }

  public get settlementId() {
    return this._settlement_id;
  }

  public get settlementLineItemId() {
    return this._settlement_line_item_id;
  }

  public get addOnChargeId() {
    return this._add_on_charge_id;
  }

  public get rutterItemId() {
    return this._rutter_item_id;
  }

  public get source() {
    return this._source;
  }

  public get reasoning() {
    return this._reasoning;
  }

  public get confidence() {
    return this._confidence;
  }

  public get issues() {
    return this._issues;
  }

  public get rutterItem() {
    return this._rutter_item;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get isAiSuggested() {
    if (!this._source || !isEnumValue(RutterLineItemMatchSource, this._source)) {
      return false;
    }

    return isAiSuggestedBySource[this._source];
  }

  public get isManual() {
    return this._source === RutterLineItemMatchSource.MANUAL;
  }

  public static parse(proto: Rutter_LineItemMatch_Read) {
    return new RutterLineItemMatch(
      proto.id,
      proto.settlement_id,
      proto.settlement_line_item_id,
      proto.add_on_charge_id,
      proto.rutter_item_id,
      proto.source,
      proto.reasoning,
      proto.confidence,
      proto.issues,
      proto.rutter_item ? RutterItem.parse(proto.rutter_item) : null,
      dayjs(proto.updated_at),
    );
  }
}

class RutterItemRuleBacktest {
  constructor(
    private _total_lines: number,
    private _tested_lines: number,
    private _item_lines: number,
    private _needs_item_lines: number,
    private _not_covered_lines: number,
  ) {}

  public get totalLines() {
    return this._total_lines;
  }

  public get testedLines() {
    return this._tested_lines;
  }

  public get itemLines() {
    return this._item_lines;
  }

  public get needsItemLines() {
    return this._needs_item_lines;
  }

  public get notCoveredLines() {
    return this._not_covered_lines;
  }

  public static parse(proto: Rutter_ItemRuleBacktest) {
    return new RutterItemRuleBacktest(
      proto.total_lines,
      proto.tested_lines,
      proto.item_lines,
      proto.needs_item_lines,
      proto.not_covered_lines,
    );
  }
}

export class RutterItemRule {
  constructor(
    private _id: string,
    private _side: SettlementExportSide,
    private _line_kind: RutterItemRuleLineKind,
    private _instructions: Nullable<string>,
    private _suggested_instructions: Nullable<string>,
    private _suggestion_reason: Nullable<string>,
    private _suggestion_backtest: Nullable<RutterItemRuleBacktest>,
    private _suggested_at: Nullable<Dayjs>,
    private _updated_at: Dayjs,
  ) {}

  public get id() {
    return this._id;
  }

  public get side() {
    return this._side;
  }

  public get lineKind() {
    return this._line_kind;
  }

  public get instructions() {
    return this._instructions;
  }

  public get suggestedInstructions() {
    return this._suggested_instructions;
  }

  public get suggestionReason() {
    return this._suggestion_reason;
  }

  public get suggestionBacktest() {
    return this._suggestion_backtest;
  }

  public get suggestedAt() {
    return this._suggested_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public static parse(proto: Rutter_ItemRule_Read) {
    return new RutterItemRule(
      proto.id,
      proto.side,
      proto.line_kind,
      proto.instructions,
      proto.suggested_instructions,
      proto.suggestion_reason,
      proto.suggestion_backtest
        ? RutterItemRuleBacktest.parse(proto.suggestion_backtest)
        : null,
      proto.suggested_at ? dayjs(proto.suggested_at) : null,
      dayjs(proto.updated_at),
    );
  }
}
